import { DEFAULT_STREET_SURVEY_KEY, isValidStreetSurveyKey } from './streetSurveys.js';
/** 既定の観点の key (机上調査の既定の調査と同じ値)。 */
export const DEFAULT_POST_ANALYSIS_KEY = DEFAULT_STREET_SURVEY_KEY;
/** 投稿に追加の観点ごとの最新結果を持つフィールド名。 */
export const POST_ANALYSIS_RESULTS_FIELD = 'analysisResults';
/**
 * 追加の観点の上限。観点は投稿ごとに順に解析するため、多いと写真のぼかし処理と合わせて関数の制限時間
 * (300 秒) を超えうる (docs/29 レビュー)。超えた分は無視する。
 */
export const MAX_ADDITIONAL_POST_ANALYSES = 3;
/** 既定の観点の表示名の既定値。 */
export const DEFAULT_POST_ANALYSIS_LABEL = 'AI 解析';
/**
 * 観点ごとに持てる地区の上書き (docs/29 §3-1、2026-10-15 改訂)。既定の観点は `district.aiAnalysis` 直下に、
 * 追加の観点は `postAnalyses[i]` の中に持つ。統合エディタ (地区モード) が扱う項目と同じ。
 */
export const POST_ANALYSIS_OVERRIDE_KEYS = [
    'outputFormatIdOverride',
    'promptSectionOverrides',
    'outputFieldsOverride',
    'inputFieldKeys',
    'generationConfigOverride',
    'categoryMasterIdsOverride',
    'titleGenerationOverride',
    'validatorsOverride',
    'promptInputMappingOverride',
];
/** 追加の観点の key として使えるか。 */
export function isValidPostAnalysisKey(key) {
    return isValidStreetSurveyKey(key);
}
/** 既定の観点か (未指定・空も既定)。 */
export function isDefaultPostAnalysis(key) {
    return !key || key === DEFAULT_POST_ANALYSIS_KEY;
}
/** 追加の観点 (壊れた定義・重複・解析設定の無いものは除く)。 */
export function listAdditionalPostAnalyses(districtAi) {
    const seen = new Set();
    const out = [];
    const raw = districtAi?.postAnalyses;
    if (!Array.isArray(raw))
        return out;
    for (const a of raw) {
        if (!a || typeof a.key !== 'string' || !isValidPostAnalysisKey(a.key) || seen.has(a.key))
            continue;
        if (typeof a.settingsId !== 'string' || !a.settingsId.trim())
            continue;
        if (out.length >= MAX_ADDITIONAL_POST_ANALYSES)
            break;
        seen.add(a.key);
        out.push({
            ...pickOverrides(a),
            key: a.key,
            label: typeof a.label === 'string' && a.label.trim() ? a.label.trim() : a.key,
            settingsId: a.settingsId.trim(),
        });
    }
    return out;
}
/** 上書きの項目だけを取り出す (undefined は落とす)。 */
export function pickOverrides(src) {
    const out = {};
    for (const k of POST_ANALYSIS_OVERRIDE_KEYS) {
        const v = src?.[k];
        if (v !== undefined && v !== null)
            out[k] = v;
    }
    return out;
}
/** 表示に要る上書きだけ (投稿アプリへ渡す、プロンプトなどは渡さない)。 */
function displayOverrides(src) {
    return {
        ...(typeof src?.outputFormatIdOverride === 'string' && src.outputFormatIdOverride
            ? { outputFormatIdOverride: src.outputFormatIdOverride }
            : {}),
        ...(Array.isArray(src?.outputFieldsOverride)
            ? { outputFieldsOverride: src.outputFieldsOverride }
            : {}),
    };
}
/** 観点の一覧 (既定の観点が先頭)。 */
export function listPostAnalysisOptions(districtAi) {
    const defaultLabel = typeof districtAi?.defaultAnalysisLabel === 'string' && districtAi.defaultAnalysisLabel.trim()
        ? districtAi.defaultAnalysisLabel.trim()
        : DEFAULT_POST_ANALYSIS_LABEL;
    return [
        {
            key: DEFAULT_POST_ANALYSIS_KEY,
            label: defaultLabel,
            isDefault: true,
            settingsId: null,
            ...displayOverrides(districtAi),
        },
        ...listAdditionalPostAnalyses(districtAi).map((a) => ({
            key: a.key,
            label: a.label,
            isDefault: false,
            settingsId: a.settingsId,
            ...displayOverrides(a),
        })),
    ];
}
/**
 * 観点の解析に使う地区の AI 解析設定の形。既定の観点は地区の設定そのもの、追加の観点は解析設定と観点自身の上書き
 * (既定の観点の上書きは使わない)。見つからない観点は null。
 */
export function resolvePostAnalysisAiConfig(districtAi, key) {
    if (isDefaultPostAnalysis(key))
        return districtAi ?? null;
    const def = listAdditionalPostAnalyses(districtAi).find((a) => a.key === key);
    if (!def)
        return null;
    return {
        ...pickOverrides(def),
        enabled: districtAi?.enabled !== false,
        settingsId: def.settingsId,
    };
}
/** 投稿の追加の観点の結果 (無ければ null)。 */
export function readPostAnalysisResult(post, key) {
    const results = post?.[POST_ANALYSIS_RESULTS_FIELD];
    if (!results || typeof results !== 'object' || Array.isArray(results))
        return null;
    const r = results[key];
    if (!r || typeof r !== 'object' || Array.isArray(r))
        return null;
    const summary = r;
    const aiAnalysis = summary.aiAnalysis &&
        typeof summary.aiAnalysis === 'object' &&
        !Array.isArray(summary.aiAnalysis)
        ? summary.aiAnalysis
        : null;
    if (!aiAnalysis)
        return null;
    return {
        aiAnalysis,
        runId: typeof summary.runId === 'string' ? summary.runId : null,
        analyzedAt: typeof summary.analyzedAt === 'string' ? summary.analyzedAt : null,
    };
}
/**
 * 投稿詳細に出す出力フォーマットの形 (docs/29 §5-1、pure)。地区の「この地区だけの出力項目」があれば項目を差し替える。
 */
export function applyOutputFieldsOverride(schema, outputFieldsOverride) {
    if (!schema || !Array.isArray(outputFieldsOverride) || outputFieldsOverride.length === 0)
        return schema;
    return { ...schema, fields: outputFieldsOverride };
}
/** 投稿詳細に出さない項目の key (`showInPostDetail === false` の最上位の項目)。 */
export function hiddenPostDetailFieldKeys(schema) {
    const out = new Set();
    for (const f of schema?.fields ?? []) {
        const rf = f.kind === 'enum-array' || f.kind === 'code-list-string' ? f.reasonField : undefined;
        if (f.showInPostDetail === false) {
            out.add(f.key);
            if (rf)
                out.add(rf.key);
        }
        else if (rf?.showInPostDetail === false) {
            out.add(rf.key);
        }
    }
    return out;
}
//# sourceMappingURL=postAnalyses.js.map